import { type ReactNode, useEffect } from 'react';
import { useLocation } from 'wouter';

import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { UnifiedSidebar } from '@/components/sidebar';
import { SettingsSidebar } from '@/components/sidebar/SettingsSidebar';
import { GlobalHeader } from '@/components/GlobalHeader';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { MobileTabBar } from '@/components/MobileTabBar';
import { UserMenu } from '@/components/sidebar/UserMenu';
import { AccountRecordProvider } from '@/pages/account/AccountRecordContext';
import { AccountConfirmations } from '@/pages/account/AccountOverlays';

interface SettingsLayoutProps {
  children: ReactNode;
}

/**
 * AR-390 — the shell for every page of the Settings module.
 *
 * The same structure the Account center uses: GlobalHeader → [ UnifiedSidebar
 * (Level 1) | SettingsSidebar (Level 2) | content ]. Level 1 stays collapsed
 * on these pages, which is UnifiedSidebar's own default off an admin route.
 *
 * The record provider wraps the whole shell because the pages and the column
 * read one record — the settings search opens a card on a page, so the two
 * cannot each hold their own copy. It is the SAME provider the Account center
 * mounts: the two modules edit one member record, split across two columns
 * (D-ACCT-28), and a second copy could drift from it.
 */
export default function SettingsLayout({ children }: SettingsLayoutProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { favicon } = useTheme();
  const [location, navigate] = useLocation();

  useEffect(() => {
    if (!favicon) return;
    const el = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    if (el) {
      el.href = favicon;
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = favicon;
      document.head.appendChild(link);
    }
  }, [favicon]);

  const shouldRedirect = !isAuthenticated && !isLoading;

  useEffect(() => {
    if (shouldRedirect) navigate('/login');
  }, [shouldRedirect, navigate]);

  if (!isAuthenticated) {
    if (isLoading) return <div>Loading...</div>;
    return null;
  }

  return (
    <AccountRecordProvider>
      <div
        data-settings-shell
        data-location={location}
        className="flex h-screen flex-col overflow-hidden bg-gray-50 dark:bg-[#101415]"
      >
        <GlobalHeader />
        <ImpersonationBanner />
        <div className="flex min-h-0 flex-1" data-settings-body>
          <div className="hidden md:flex">
            <UnifiedSidebar />
          </div>
          <div className="hidden md:flex">
            <SettingsSidebar />
          </div>
          <main className="flex-1 overflow-auto bg-white dark:bg-[#101415]" data-settings-main>
            {children}
          </main>
        </div>
        {/* The phone shell: the two rails leave the flow, the primary
            destinations become a bottom tab bar, and the Settings column moves
            into the More sheet above it. The user menu is the sheet's footer,
            which is where Account, Settings and Notifications sit on a phone
            (D-ACCT-28). */}
        <MobileTabBar footer={<UserMenu expanded inline />}>
          {(close) => <SettingsSidebar inDrawer onNavigated={close} />}
        </MobileTabBar>
        <AccountConfirmations />
      </div>
    </AccountRecordProvider>
  );
}
